<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Disconnect Aptly?</Text>
          <Text variant="body-large" color="secondary" align="center">
            Are you sure you want to disconnect your Aptly integration?
          </Text>
        </View>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" @click="disconnect" :loading="isDisconnecting">Disconnect</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { Button, Modal, Text, View, WhatToExpectCard, WhatToExpectItem, useAlertsStore } from '@tallo/design-system';
import { aptlyService, useCompanyStore } from '@tallo/investor/company';

const emit = defineEmits(['disconnected']);
const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();
const companyStore = useCompanyStore();
const isDisconnecting = ref(false);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'x-close-circle',
    title: 'Stop syncing property data',
    description: 'Property updates from Aptly will no longer be synced to Tallo.',
  },
  {
    icon: 'x-close-circle',
    title: 'Remove feed URL',
    description: 'Your Aptly feed URL will be removed from Tallo.',
  },
  {
    icon: 'x-close-circle',
    title: 'Manual property management',
    description: 'You will need to manage property listings manually in Tallo.',
  },
];

async function disconnect() {
  try {
    isDisconnecting.value = true;
    await aptlyService.disconnect();
    await companyStore.refresh();

    alertsStore.success('Aptly disconnected successfully');
    emit('disconnected');
    close();
  } catch (error) {
    console.error('Failed to disconnect Aptly:', error);
    alertsStore.error('Failed to disconnect Aptly');
  } finally {
    isDisconnecting.value = false;
  }
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
